import { Add, WatchLater } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { enUS, plPL } from '@mui/x-date-pickers/locales'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/pl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DatePickerWithArrows from '../../components/DatePickerWithArrows/DatePickerWithArrows'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import AutocompleteInput from '../../components/Form/Inputs/AutocompleteInput'
import CheckboxInput from '../../components/Form/Inputs/CheckboxInput'
import TextInput from '../../components/Form/Inputs/TextInput'
import MasonryTile from '../../components/Masonry/MasonryTile'
import MasonryTileTitle from '../../components/Masonry/MasonryTileTitle'
import { defaultPageSize } from '../../config/defaultPagination'
import { useEditMode, useNotification } from '../../store/redux/hooks'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import { ProjectAcronym, useGetAcronymsQuery, useGetLastProjectsQuery } from '../../store/redux/reducers/projectsSlice'
import { getCurrentDefaultProject, getCurrentRemoteWorkMode } from '../../store/redux/reducers/settingsSlice'
import { useGetCurrentUserQuery } from '../../store/redux/reducers/usersSlice'
import { useAddUsersWorklogEntryMutation } from '../../store/redux/reducers/worklogSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import { getDefaultWorklogEntryValues, WorklogEntryFormValues } from '../../store/types/form/WorklogEntryFormValues'
import { createErrorMessage } from '../../store/utils'
import { filterOptions } from '../../store/utils/filterProjectsAcronymsOptions'
import { getNumericTimeValue } from '../../store/utils/worklogUtils'
import {
  StyledMasonrySectionCentered,
  StyledMasonrySectionColumn,
  StyledMasonrySectionRow,
} from '../../styles/MasonryStyles'

const AddWorklogForm = () => {
  const { t, i18n } = useTranslation()
  const { displayNotification } = useNotification()
  const remoteWorkModeFlag = useSelector(getCurrentRemoteWorkMode)
  const defaultProject = useSelector(getCurrentDefaultProject)
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setError,
  } = useForm<WorklogEntryFormValues>({
    defaultValues: getDefaultWorklogEntryValues(null, remoteWorkModeFlag, defaultProject),
  })

  const supportedLanguages = ['pl', 'en'] as const
  type SupportedLang = (typeof supportedLanguages)[number]

  const localeMap: Record<SupportedLang, typeof plPL.components.MuiLocalizationProvider.defaultProps.localeText> = {
    pl: plPL.components.MuiLocalizationProvider.defaultProps.localeText,
    en: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
  }

  const normalizeLang = (lang: string): SupportedLang => {
    const shortLang = lang.split('-')[0]
    return supportedLanguages.includes(shortLang as SupportedLang) ? (shortLang as SupportedLang) : 'en'
  }

  const currentLang = normalizeLang(i18n.language)
  const initialDate = useRef(dayjs())
  const [day, setDay] = useState<Dayjs>(dayjs())

  const { data: currentUserWithSettings } = useGetCurrentUserQuery({})

  const settings = useMemo(() => currentUserWithSettings?.settings, [currentUserWithSettings])
  const workHoursSortByFavoriteFlag = settings?.workHoursSortByFavorite || false

  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const getAcronymProps = useCallback(() => {
    return {
      page: page,
      size: defaultPageSize,
      date: day.format(DateFormat.YearMonthDay),
      acronym: searchedAcronym,
      loggable: true,
      sortByWorkHours: !workHoursSortByFavoriteFlag,
      sortByFavorites: workHoursSortByFavoriteFlag,
    }
  }, [page, day, searchedAcronym, workHoursSortByFavoriteFlag])

  const [acronymProps, setAcronymProps] = useState(getAcronymProps())
  const { data: projectsResponse } = useGetAcronymsQuery(acronymProps)
  const { data: projectIdsResponse, refetch: refetchIds } = useGetLastProjectsQuery({
    date: day.format(DateFormat.YearMonthDay),
  })
  const [favoriteProjects, setFavoriteProjects] = useState<ProjectAcronym[]>([])
  const [projects, setProjects] = useState<ProjectAcronym[]>([])
  const [projectIds, setProjectIds] = useState<string[]>([])
  const { setIsEditMode } = useEditMode()

  useEffect(() => {
    reset(getDefaultWorklogEntryValues(null, remoteWorkModeFlag, defaultProject))
  }, [defaultProject, remoteWorkModeFlag, reset])

  const onSelectBottomScroll = () => {
    if (!projectsResponse?.last) {
      handleFetchMoreData()
    }
  }

  const handleFetchMoreData = () => {
    setPage(prev => prev + 1)
  }

  useEffect(() => {
    setAcronymProps(getAcronymProps())
  }, [getAcronymProps, page, searchedAcronym])

  useMemo(() => {
    if (projectsResponse) {
      setProjects(prevProjects => {
        const existingProjectIds = new Set(prevProjects.map(project => project.id))
        const newProjects = projectsResponse.content.filter(project => !existingProjectIds.has(project.id))
        return [...prevProjects, ...newProjects]
      })
    }
  }, [projectsResponse])

  useMemo(() => {
    if (projectIdsResponse) {
      setProjectIds(projectIdsResponse)
    }
  }, [projectIdsResponse])

  const { data: favoriteProjectsResponse, isSuccess } = useGetAcronymsQuery({
    page: 0,
    size: 100,
    favorite: true,
  })

  useEffect(() => {
    if (isSuccess) {
      setFavoriteProjects(favoriteProjectsResponse.content)
    }
  }, [favoriteProjectsResponse, isSuccess])

  const checkIfLastAddedProject = useCallback(
    (projectId: string) => {
      return projectIds.some(id => id === projectId)
    },
    [projectIds]
  )

  const checkIfFavoriteProject = useCallback(
    (projectId: string) => {
      return favoriteProjects.some(project => project.id === projectId)
    },
    [favoriteProjects]
  )

  const isActiveProject = (project: ProjectAcronym) => !['INACTIVE', 'FINISHED'].includes(project.projectStatus)

  const sortedProjects = useMemo(() => {
    const isRelevantAndActive = (project: ProjectAcronym) => {
      const isRelevant = workHoursSortByFavoriteFlag
        ? checkIfFavoriteProject(project.id)
        : checkIfLastAddedProject(project.id)
      return isRelevant && isActiveProject(project)
    }

    return [
      ...projects.filter(isRelevantAndActive),
      ...projects.filter(project => !isRelevantAndActive(project) && isActiveProject(project)),
    ]
  }, [checkIfFavoriteProject, checkIfLastAddedProject, projects, workHoursSortByFavoriteFlag])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedAcronym(value)
    }, 500)
  }, [])

  const translate = useCallback(() => {
    const getTranslatedMessage = (fieldName: string, type: string) => {
      const translationMappings: Record<string, Record<string, string>> = {
        hours: {
          required: 'page.worklog.form.timeSpentRequiredError',
          pattern: 'page.worklog.form.timeSpentFormatError',
        },
        project: {
          required: 'page.worklog.form.projectRequiredError',
        },
        description: {
          maxLength: 'page.worklog.form.descriptionLengthError',
        },
      }

      const translationKey =
        translationMappings[fieldName as keyof typeof translationMappings] &&
        translationMappings[fieldName as keyof typeof translationMappings][type]

      return translationKey ? t(translationKey) : ''
    }

    Object.entries(errors).forEach(([fieldName, fieldRules]) => {
      if (fieldRules !== undefined && fieldRules.type !== undefined) {
        const translatedMessage = getTranslatedMessage(fieldName, fieldRules.type.toString())
        setError(fieldName as keyof WorklogEntryFormValues, {
          type: fieldRules.type.toString(),
          message: translatedMessage,
        })
      }
    })
  }, [errors, setError, t])

  useEffect(() => {
    translate()
  }, [i18n.language, translate])

  useEffect(() => {
    setIsEditMode(true)
  })

  const [addEntry] = useAddUsersWorklogEntryMutation()

  const currentUser = useSelector(getCurrentUser)

  const id = currentUser?.id === undefined ? '' : currentUser?.id

  const handleAddEntry: SubmitHandler<WorklogEntryFormValues> = useCallback(
    async data => {
      const selectedDate = day || dayjs()
      try {
        await addEntry({
          employeeId: id,
          entry: {
            date: selectedDate.format(DateFormat.YearMonthDay),
            hours: getNumericTimeValue(data.hours),
            description: data.description?.trim(),
            projectId: data.project?.value,
            remote: data.remote,
          },
        }).unwrap()
        reset(getDefaultWorklogEntryValues(null, remoteWorkModeFlag, defaultProject))
        setDay(dayjs())
        displayNotification({ message: t('page.worklog.alert.addEntrySuccess'), type: 'success' })
        setIsEditMode(false)
        refetchIds()
      } catch (error) {
        let errorDescription = createErrorMessage(error)
        displayNotification({
          message: `${t('page.worklog.alert.addEntryError')}: ${errorDescription}`,
          type: 'error',
        })
        reset(data)
      }
    },
    [day, addEntry, id, reset, remoteWorkModeFlag, defaultProject, displayNotification, t, setIsEditMode, refetchIds]
  )

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const checkUnsavedChanges = () => {
    return isDirty || day.format(DateFormat.DayMonthYear) !== initialDate.current.format(DateFormat.DayMonthYear)
  }

  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const activeElement = document.activeElement
        if (formRef.current?.contains(activeElement)) {
          e.preventDefault()
          handleSubmit(handleAddEntry)()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleSubmit, handleAddEntry])

  const options = useMemo(() => {
    const mappedSortedProjects = sortedProjects.map(project => ({ label: project.acronym, value: project.id }))
    const isDefaultProjectInOptions =
      defaultProject && !sortedProjects.some(project => project.id === defaultProject.value)

    return isDefaultProjectInOptions ? [...mappedSortedProjects, defaultProject] : mappedSortedProjects
  }, [defaultProject, sortedProjects])

  return (
    <MasonryTile ref={formRef}>
      <MasonryTileTitle title={t('components.tile.title.worklog')} icon={WatchLater} />

      <StyledMasonrySectionColumn>
        <Typography variant="subtitle1" component="h2" fontWeight={600}>
          {t('components.tile.header.workingHours')}
        </Typography>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={currentLang}
          localeText={localeMap[currentLang]}
        >
          <DatePickerWithArrows
            label={t('components.table.cell.date')}
            value={day}
            onChange={date => (date === null ? setDay(dayjs()) : setDay(date))}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </LocalizationProvider>
      </StyledMasonrySectionColumn>

      <StyledMasonrySectionColumn>
        <Typography variant="subtitle1" component="h3" fontWeight={600}>
          {t('components.tile.header.newRecord')}
        </Typography>

        <StyledMasonrySectionColumn>
          <StyledMasonrySectionRow>
            <TextInput
              name={'hours'}
              control={control}
              label={t('page.worklog.form.timeSpent')}
              placeholder={t('page.worklog.form.timeSpentPlaceholder')}
              rules={{
                required: t('page.worklog.form.timeSpentRequiredError'),
                pattern: {
                  value: /^\s*(?:(([1-9]|1[0-9]|2[0-3])h)?\s*(30m)?)\s*$/,
                  message: t('page.worklog.form.timeSpentFormatError'),
                },
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <CheckboxInput name={'remote'} control={control} label={t('page.worklog.form.remote')} />
            </Box>
          </StyledMasonrySectionRow>

          <AutocompleteInput
            options={options}
            onSelectBottomScroll={onSelectBottomScroll}
            name={'project'}
            control={control}
            label={t('page.worklog.form.project')}
            rules={{ required: t('page.worklog.form.projectRequiredError') }}
            noOptionsText={t('page.worklog.form.projectsNotFound')}
            groupBy={
              workHoursSortByFavoriteFlag
                ? option => {
                    const isFavorite = checkIfFavoriteProject(option.value)
                    return isFavorite
                      ? t('page.worklog.form.yourFavoriteProjects')
                      : t('page.worklog.form.otherProjects')
                  }
                : option => {
                    const hasHoursInCurrentMonth = checkIfLastAddedProject(option.value)
                    return hasHoursInCurrentMonth
                      ? t('page.worklog.form.yourLastProjects')
                      : t('page.worklog.form.otherProjects')
                  }
            }
            listboxMaxHeight={250}
            handleInputChange={handleInputChange}
            filterOptions={(options, state) => filterOptions(options, state, sortedProjects)}
          />

          <TextInput
            name={'description'}
            control={control}
            label={t('page.worklog.form.largeDescription')}
            aria-label={t('page.worklog.form.largeDescription')}
            placeholder={t('page.worklog.form.descriptionPlaceholder')}
            maxLength={1000}
            rules={{
              maxLength: { value: 1000, message: t('page.worklog.form.descriptionLengthError') },
            }}
            multiline
            showCharCounter
          />
        </StyledMasonrySectionColumn>
      </StyledMasonrySectionColumn>

      <StyledMasonrySectionCentered>
        <LoadingButton
          onClick={handleSubmit(handleAddEntry)}
          variant="contained"
          startIcon={<Add />}
          size="medium"
          disabled={!isDirty || isSubmitting}
          loading={isSubmitting}
        >
          {t('components.button.addNewRecord')}
        </LoadingButton>
      </StyledMasonrySectionCentered>

      <DiscardChangesDialog
        checkUnsavedChanges={checkUnsavedChanges}
        open={isCancelDialogOpen}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </MasonryTile>
  )
}

export default AddWorklogForm
